import { debounce } from "lodash";
import { useEffect, useMemo, useRef, useState } from "react";
import type { MutableRefObject, RefCallback } from "react";
import ResizeObserver from "resize-observer-polyfill";

type OnResize = (width: number, height: number) => void;

type ObserverArgs = {
    onResize?: OnResize;
    /**
     * Which size to report. `"content"`, the default, is the content box,
     * and ignores changes of `pixelDelta` or less. `"border"` is the box the
     * element renders, measured as soon as it is attached and reported on
     * every change however small, for layout that must never see a zero or a
     * stale size.
     */
    box?: "content" | "border";
    /** How long a size has to hold before it is reported. */
    debounceWait?: number;
    /** Defaults to 6 for the content box and 0 for the border box. */
    pixelDelta?: number;
    name?: string;
    enableLogging?: boolean;
};

/**
 * A callback ref that also holds the element, so it can be passed to `ref`
 * and read through `.current` like a ref object.
 */
export type ObservedElementRef<T> = RefCallback<T> & MutableRefObject<T | null>;

function createObservedElementRef<T>(
    onChange: (element: T | null) => void,
): ObservedElementRef<T> {
    const ref = ((element: T | null) => {
        ref.current = element;
        onChange(element);
    }) as ObservedElementRef<T>;
    ref.current = null;
    return ref;
}

const borderBoxOf = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    return { width: Math.round(rect.width), height: Math.round(rect.height) };
};

/**
 * Reports the size of the element `theRef` is attached to, once it has
 * changed by more than `pixelDelta` and held for `debounceWait`.
 *
 * `theRef` is a callback ref, so an element that mounts after the first
 * render, or replaces another, is observed as soon as it is attached.
 */
export function useResizeObserver<T extends HTMLElement>(
    opts: ObserverArgs = {},
) {
    const [element, setElement] = useState<T | null>(null);
    const [elementWidth, setElementWidth] = useState<number | undefined>(0);
    const [elementHeight, setElementHeight] = useState<number | undefined>(0);
    const prevElementWidth = useRef<number>(0);
    const prevElementHeight = useRef<number>(0);
    const handlerRef = useRef<OnResize | undefined>(opts.onResize);

    const [theRef] = useState(() => createObservedElementRef<T>(setElement));

    useEffect(() => {
        handlerRef.current = opts.onResize;
    }, [opts.onResize]);

    const debouncedSizeUpdate = useMemo(
        () =>
            debounce(
                (width: number, height: number) => {
                    setElementWidth(width);
                    setElementHeight(height);
                    handlerRef.current?.(width, height);
                },
                opts.debounceWait ?? 10,
                { trailing: true },
            ),
        [opts.debounceWait],
    );

    useEffect(() => () => debouncedSizeUpdate.cancel(), [debouncedSizeUpdate]);

    const { box = "content", name, enableLogging } = opts;
    const pixelDelta = opts.pixelDelta ?? (box === "border" ? 0 : 6);
    useEffect(() => {
        if (!element) return;

        if (box === "border") {
            // Measured straight away, so a mounted element never reports zero.
            const { width, height } = borderBoxOf(element);
            prevElementWidth.current = width;
            prevElementHeight.current = height;
            setElementWidth(width);
            setElementHeight(height);
            handlerRef.current?.(width, height);
        }

        const observer = new ResizeObserver((entries) => {
            if (!entries.length) return;

            const { contentRect } = entries[0];
            const { width: newWidth, height: newHeight } =
                box === "border"
                    ? borderBoxOf(element)
                    : {
                          width: Math.round(contentRect.width ?? 0),
                          height: Math.round(contentRect.height ?? 0),
                      };

            if (
                Math.abs(prevElementHeight.current - newHeight) <= pixelDelta &&
                Math.abs(prevElementWidth.current - newWidth) <= pixelDelta
            ) {
                return;
            }

            if (name && enableLogging) {
                console.log(`Resizing ${name}: W=${newWidth}, H=${newHeight}`);
            }

            prevElementWidth.current = newWidth;
            prevElementHeight.current = newHeight;
            debouncedSizeUpdate(newWidth, newHeight);
        });
        observer.observe(element);

        return () => observer.disconnect();
    }, [element, box, debouncedSizeUpdate, pixelDelta, name, enableLogging]);

    return useMemo(
        () => ({ theRef, element, elementWidth, elementHeight }),
        [theRef, element, elementWidth, elementHeight],
    );
}
